import React, { useState, useEffect, useRef } from 'react';
import ListingAgentAvatar from './ListingAgentAvatar';
import ListingPropertyLink from './ListingPropertyLink';
import ReportConversationModal from './ReportConversationModal';

const ListingChatModal = ({ chat, onClose, onSend, sending, currentUid, addToast, onOpenListing }) => {
  const initialDraft = String(chat?.initialDraft || "").trim();
  const [draft, setDraft] = useState(initialDraft);
  const [reportOpen, setReportOpen] = useState(false);
  const threadRef = useRef(null);
  const conv = chat?.conversation;
  const messages = chat?.messages || [];
  const intent = chat?.intent || "message";
  const agentName = String(conv?.agentName || "listing agent").trim();
  const agentAvatarUrl = String(conv?.agentAvatarUrl || chat?.property?.agentAvatarUrl || "").trim() || null;
  const headerPrefix = intent === "consultation" ? "Consultation with " : "Message ";

  useEffect(() => {
    setDraft(initialDraft);
  }, [conv?.id, initialDraft]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages.length, chat]);

  if (!conv) return null;

  const submit = () => {
    const text = draft.trim();
    if (!text || sending) return;
    onSend(text);
    setDraft("");
  };

  return (
    <div className="modal-overlay active listing-chat-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="listing-chat-title">
        <div className="listing-chat-modal-header">
          <ListingAgentAvatar name={conv.agentName} avatarUrl={agentAvatarUrl} />
          <div className="listing-chat-modal-header-copy">
            <h2 id="listing-chat-title">{headerPrefix}{agentName}</h2>
            <p>
              <ListingPropertyLink
                propertyId={conv.propertyId}
                title={conv.propertyTitle}
                onOpenListing={onOpenListing}
              />
            </p>
          </div>
          {conv.id ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginRight: '0.35rem', color: 'var(--danger)', fontSize: '0.78rem' }}
              onClick={() => setReportOpen(true)}
              title="Report fraud or suspicious activity"
            >
              <i className="fas fa-flag"></i>
            </button>
          ) : null}
          <button type="button" className="listing-chat-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="listing-chat-thread" ref={threadRef}>
          {messages.length === 0 ? (
            <div className="listing-chat-empty">
              <i className="fas fa-comments" aria-hidden="true"></i>
              <p>
                {intent === "consultation"
                  ? "Introduce yourself and send your consultation request. The agent will reply here in CoOwn chat."
                  : "Say hello — your message goes directly to the listing agent in CoOwn chat."}
              </p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.senderUid === currentUid;
              return (
                <div key={m.id} className={`listing-chat-bubble${mine ? " listing-chat-bubble--mine" : " listing-chat-bubble--theirs"}`}>
                  {m.text}
                  <small>{m.senderName || (mine ? "You" : conv.agentName)}</small>
                </div>
              );
            })
          )}
        </div>
        <div className="listing-chat-compose">
          <textarea
            rows={2}
            placeholder={intent === "consultation" ? "Tell the agent what you'd like to discuss…" : "Write your message…"}
            value={draft}
            disabled={sending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button type="button" className="listing-chat-send-btn" disabled={sending || !draft.trim()} onClick={submit} aria-label="Send message">
            {sending ? <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> : <i className="fas fa-paper-plane" aria-hidden="true"></i>}
          </button>
        </div>
        <p className="listing-chat-modal-note">Inquiries stay in CoOwn chat — we never open your email app for listing messages.</p>
      </div>
      {reportOpen && (
        <ReportConversationModal
          conversation={conv}
          addToast={addToast}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
};

export default ListingChatModal;
